import OnboardingSheet from "../../OnboardingSheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useOnboardingDocument } from "../hooks/useOnboardingDocument";
import { Button } from "@/components/ui/button";
import { Loader2, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface DocumentStepProps {
  open: boolean;
  onClose: () => void;
  isPending?: boolean;
  documentType?: string;
  isEditing?: boolean;
}

export function DocumentStep({
  open,
  onClose,
  documentType,
  isPending = false,
  isEditing,
}: DocumentStepProps) {
  const { onSubmit, handleSubmit, register, errors, isSendingDocuments } =
    useOnboardingDocument({ onClose, mode: isEditing ? "edit" : "create" });

  const { t } = useTranslation("onboarding");

  const renderEditHeaderAlert = () => (
    <Alert variant="warning" className="h-auto flex items-start gap-2">
      <Info className="h-5 w-5 flex-shrink-0" color="#FACC15" />
      <div>
        <AlertTitle className="text-white">
          {t("mundpay_team_notes")}
        </AlertTitle>
        <AlertDescription>
          {t("photo_illegible")}
          <br />
          {t("selfie_wrong_document")}
        </AlertDescription>
      </div>
    </Alert>
  );

  const renderWarningAlert = () => (
    <Alert variant="warning" className="w-full max-md:order-2">
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_2158_3873)">
          <path
            d="M10.0003 13.3334V10.0001M10.0003 6.66675H10.0087M18.3337 10.0001C18.3337 14.6025 14.6027 18.3334 10.0003 18.3334C5.39795 18.3334 1.66699 14.6025 1.66699 10.0001C1.66699 5.39771 5.39795 1.66675 10.0003 1.66675C14.6027 1.66675 18.3337 5.39771 18.3337 10.0001Z"
            stroke="#FACC15"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <defs>
          <clipPath id="clip0_2158_3873">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <AlertDescription>{t("make_sure")}</AlertDescription>
    </Alert>
  );

  return (
    <OnboardingSheet
      isPending={isPending}
      onClose={onClose}
      open={open}
      hasHeader={!isEditing}
    >
      {isEditing && (
        <SheetHeader className='md:p-6 p-4 border-b border-b-colorBorder'>
          <SheetTitle className='flex gap-2'>{t('new_submission')}</SheetTitle>
          {renderEditHeaderAlert()}
        </SheetHeader>
      )}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={`space-y-8 flex-col flex justify-between ${
          !isEditing ? 'h-[calc(100vh-140px)]' : 'flex-1 h-[80%]'
        }`}
      >
        <section className={`${isEditing ? 'md:p-6 p-4 flex-1' : 'p-6'}`}>
          <SheetTitle>{t('documents')}</SheetTitle>
          <article>
            <div className='space-y-1 mt-4'>
              <Label htmlFor='proof_of_address'>{t('proof_of_address')}</Label>
              <p className='text-muted text-sm'>
                {t('proof_of_address_description')}
              </p>
              <Input
                {...register('proof_of_address')}
                className={`text-muted ${
                  errors.proof_of_address ? 'border-error' : ''
                }`}
                id='proof_of_address'
                type='file'
                accept='.png, .jpg, .jpeg, .pdf'
              />
              <p className='text-muted text-sm'>{t('pdfOr')}</p>
              {errors.proof_of_address && (
                <p className='text-sm text-error'>
                  {errors?.proof_of_address.message}
                </p>
              )}
            </div>

            {documentType === 'business' && (
              <div className='space-y-1 mt-4'>
                <Label htmlFor='social_contract'>{t('social_contract')}</Label>
                <Input
                  accept='application/pdf'
                  {...register('social_contract')}
                  className={`text-muted ${
                    errors.social_contract ? 'border-error' : ''
                  }`}
                  id='social_contract'
                  type='file'
                />
                <p className='text-muted text-sm'>{t('pdf')}</p>
                {errors.social_contract && (
                  <p className='text-sm text-error'>
                    {String(errors?.social_contract?.message)}
                  </p>
                )}
              </div>
            )}
          </article>
        </section>

        {!isEditing ? (
          <SheetFooter className='flex gap-2 border-t border-t-colorBorder py-4 px-6 max-md:pb-24 flex-wrap'>
            {renderWarningAlert()}
            <div className='flex justify-end items-center'>
              <Button
                variant='ghost'
                className='!text-colorText'
                onClick={onClose}
                type='button'
              >
                {t('cancel')}
              </Button>
              <Button type='submit' disabled={isSendingDocuments}>
                {isSendingDocuments ? (
                  <span className='flex items-center gap-2'>
                    <Loader2 className='animate-spin' />
                    {t('saving')}
                  </span>
                ) : (
                  t('save')
                )}
              </Button>
            </div>
          </SheetFooter>
        ) : (
          <SheetFooter className='flex flex-col gap-6 border-t border-t-colorBorder py-4 px-6 max-md:pb-24 flex-wrap'>
            {renderWarningAlert()}
            <div className='flex gap-2 justify-end items-end'>
              <Button
                variant='ghost'
                className='!text-colorText'
                onClick={onClose}
                type='button'
              >
                {t('cancel')}
              </Button>
              <Button type='submit' disabled={isSendingDocuments}>
                {isSendingDocuments ? (
                  <span className='flex items-center gap-2'>
                    <Loader2 className='animate-spin' />
                    {t('saving')}
                  </span>
                ) : (
                  t('save')
                )}
              </Button>
            </div>
          </SheetFooter>
        )}
      </form>
    </OnboardingSheet>
  );
}
